import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, MAGAZZINI, STAGIONI, STATI, TIPI_INTERVENTO, MARCHI_PNEUMATICO } from "@/lib/api";
import PositionPicker from "@/components/PositionPicker";
import EuropeanLicensePlate from "@/components/EuropeanLicensePlate";
import { ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";

const todayISO = () => new Date().toISOString().slice(0, 10);

const empty = {
  client_id: "",
  cliente_nome: "",
  cliente_telefono: "",
  cliente_indirizzo: "",
  data_intervento: todayISO(),
  tipo_intervento: "Cambio gomme",
  targa: "",
  marca_veicolo: "",
  modello_veicolo: "",
  marca_pneumatico: "Pirelli",
  modello_pneumatico: "",
  misura: "",
  stagionalita: "Estivo",
  quantita: 4,
  magazzino: "Vasto",
  settore: 1,
  scaffale: "A",
  posizione: "",
  numero_scheda: "",
  numero_scheda_smaltimento: "",
  numero_scheda_riconsegna: "",
  fuori_tempo_limite: false,
  con_cerchio: false,
  data_lavorazione: todayISO(),
  note: "",
  stato: "Attivo",
};

export default function DepositoForm() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [matchHint, setMatchHint] = useState(null); // {id, nome, telefono} se trovato in DB

  // Debounce match check: quando l'utente scrive un nome, cerca match esatto
  useEffect(() => {
    const nome = (form.cliente_nome || "").trim();
    if (!nome || nome.length < 2) {
      setMatchHint(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/clients", { params: { search: nome, limit: 20 } });
        const exact = (data || []).find(
          (c) => (c.nome || "").trim().toLowerCase() === nome.toLowerCase()
        );
        setMatchHint(exact || null);
      } catch (_) {
        setMatchHint(null);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [form.cliente_nome]);

  useEffect(() => {
    if (editing) {
      api.get(`/deposits/${id}`).then(({ data }) => {
        const { cliente, ...rest } = data;
        // In modifica pre-riempi i campi cliente_* con quelli dell'anagrafica esistente
        setForm({
          ...rest,
          cliente_nome: cliente?.nome || "",
          cliente_telefono: cliente?.telefono || "",
          cliente_indirizzo: cliente?.indirizzo || "",
        });
      }).catch(() => toast.error("Errore caricamento deposito"));
    }
  }, [id, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!(form.cliente_nome || "").trim()) return toast.error("Nome cliente obbligatorio");
    if (!form.targa.trim()) return toast.error("Targa obbligatoria");
    if (!form.marca_pneumatico.trim()) return toast.error("Marca pneumatico obbligatoria");
    if (!form.misura.trim()) return toast.error("Misura obbligatoria");

    setSaving(true);
    try {
      const payload = {
        ...form,
        targa: form.targa.toUpperCase().trim(),
        quantita: Number(form.quantita) || 1,
        settore: Number(form.settore) || 1,
        cliente_nome: (form.cliente_nome || "").trim(),
        cliente_telefono: (form.cliente_telefono || "").trim(),
        cliente_indirizzo: (form.cliente_indirizzo || "").trim(),
        // Se in modifica e il nome non e' cambiato, manda anche client_id
        client_id: editing ? (form.client_id || "") : "",
      };
      if (editing) {
        await api.put(`/deposits/${id}`, payload);
        toast.success("Deposito aggiornato");
      } else {
        await api.post("/deposits", payload);
        toast.success("Deposito creato");
      }
      navigate("/depositi");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="deposito-form-page">
      <button
        onClick={() => navigate(-1)}
        data-testid="back-button"
        className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest hover:text-[#E60000]"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={3} /> Indietro
      </button>

      <div>
        <div className="text-[11px] font-black uppercase tracking-[0.3em] text-[#E60000]">
          {editing ? "Modifica" : "Nuovo"}
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter mt-1">
          Scheda Deposito
        </h1>
      </div>

      <form onSubmit={save} className="space-y-6">
        {/* Cliente */}
        <Section title="Cliente & Intervento">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Nome cliente *</Label>
              <input
                data-testid="form-cliente-nome"
                type="text"
                autoComplete="off"
                value={form.cliente_nome}
                onChange={(e) => set("cliente_nome", e.target.value)}
                placeholder="Es. Mario Rossi (scrivi liberamente)"
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] bg-white font-bold"
              />
              {matchHint && !editing && (
                <div
                  data-testid="form-cliente-match-hint"
                  className="mt-2 border-2 border-black bg-[#FFF8E5] px-3 py-2 text-xs font-bold uppercase tracking-wider"
                >
                  <span className="text-[#E60000]">CLIENTE GIA' IN ARCHIVIO</span>{" "}
                  — verra' collegato a: <span className="font-mono-data normal-case">{matchHint.nome}</span>
                  {matchHint.telefono ? ` · ${matchHint.telefono}` : ""}
                </div>
              )}
            </div>
            <div>
              <Label>Telefono cliente</Label>
              <input
                data-testid="form-cliente-telefono"
                type="text"
                autoComplete="off"
                value={form.cliente_telefono}
                onChange={(e) => set("cliente_telefono", e.target.value)}
                placeholder="Es. 3331234567"
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] bg-white font-mono-data"
              />
            </div>
            <div>
              <Label>Indirizzo cliente</Label>
              <input
                data-testid="form-cliente-indirizzo"
                type="text"
                autoComplete="off"
                value={form.cliente_indirizzo}
                onChange={(e) => set("cliente_indirizzo", e.target.value)}
                placeholder="Via, N., Citta'"
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] bg-white"
              />
            </div>
            <div>
              <Label>Data intervento *</Label>
              <input
                data-testid="form-data"
                type="date"
                value={form.data_intervento}
                onChange={(e) => set("data_intervento", e.target.value)}
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] font-mono-data"
              />
            </div>
            <div>
              <Label>Tipo intervento</Label>
              <select
                data-testid="form-tipo"
                value={form.tipo_intervento}
                onChange={(e) => set("tipo_intervento", e.target.value)}
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] bg-white font-bold"
              >
                {TIPI_INTERVENTO.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <Label>Stato</Label>
              <select
                data-testid="form-stato"
                value={form.stato}
                onChange={(e) => set("stato", e.target.value)}
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] bg-white font-bold"
              >
                {STATI.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <Label>Numero scheda</Label>
              <input
                data-testid="form-numero-scheda"
                value={form.numero_scheda || ""}
                onChange={(e) => set("numero_scheda", e.target.value)}
                placeholder="Es. SCH-2026-001"
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] font-mono-data"
              />
            </div>
            <div>
              <Label>Numero scheda smaltimento (ss)</Label>
              <input
                data-testid="form-numero-scheda-smaltimento"
                value={form.numero_scheda_smaltimento || ""}
                onChange={(e) => set("numero_scheda_smaltimento", e.target.value)}
                placeholder="Es. 710"
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] font-mono-data"
              />
            </div>
            <div>
              <Label>Numero scheda riconsegna (sr)</Label>
              <input
                data-testid="form-numero-scheda-riconsegna"
                value={form.numero_scheda_riconsegna || ""}
                onChange={(e) => set("numero_scheda_riconsegna", e.target.value)}
                placeholder="Es. 1663"
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] font-mono-data"
              />
            </div>
            <div>
              <Label>Data lavorazione</Label>
              <input
                data-testid="form-data-lavorazione"
                type="date"
                value={form.data_lavorazione || ""}
                onChange={(e) => set("data_lavorazione", e.target.value)}
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] font-mono-data"
              />
            </div>
            <div className="sm:col-span-2 grid grid-cols-2 gap-4">
              <label className="flex items-center gap-3 border-2 border-black p-3 cursor-pointer hover:bg-black hover:text-white transition-colors">
                <input
                  type="checkbox"
                  data-testid="form-con-cerchio"
                  checked={!!form.con_cerchio}
                  onChange={(e) => set("con_cerchio", e.target.checked)}
                  className="w-5 h-5 accent-[#E60000]"
                />
                <span className="font-bold uppercase text-xs tracking-widest">
                  {form.con_cerchio ? "CON cerchione" : "SENZA cerchione"}
                </span>
              </label>
              <label className="flex items-center gap-3 border-2 border-black p-3 cursor-pointer hover:bg-black hover:text-white transition-colors">
                <input
                  type="checkbox"
                  data-testid="form-ftm"
                  checked={!!form.fuori_tempo_limite}
                  onChange={(e) => set("fuori_tempo_limite", e.target.checked)}
                  className="w-5 h-5 accent-[#E60000]"
                />
                <span className="font-bold uppercase text-xs tracking-widest">
                  Fuori Tempo Limite (FTM)
                </span>
              </label>
            </div>
          </div>
        </Section>

        {/* Veicolo */}
        <Section title="Veicolo">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Targa *</Label>
              <input
                data-testid="form-targa"
                value={form.targa}
                onChange={(e) => set("targa", e.target.value.toUpperCase())}
                placeholder="AB123CD"
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] font-mono-data font-black uppercase tracking-widest"
              />
              {form.targa && (
                <div className="mt-2">
                  <EuropeanLicensePlate plate={form.targa} size="md" />
                </div>
              )}
            </div>
            <div>
              <Label>Marca veicolo</Label>
              <input
                data-testid="form-marca-veicolo"
                value={form.marca_veicolo}
                onChange={(e) => set("marca_veicolo", e.target.value)}
                placeholder="Fiat, Audi, BMW..."
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000]"
              />
            </div>
            <div>
              <Label>Modello veicolo</Label>
              <input
                data-testid="form-modello-veicolo"
                value={form.modello_veicolo}
                onChange={(e) => set("modello_veicolo", e.target.value)}
                placeholder="Panda, A3, Serie 1..."
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000]"
              />
            </div>
          </div>
        </Section>

        {/* Pneumatico */}
        <Section title="Pneumatico">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Marca pneumatico *</Label>
              <select
                data-testid="form-marca-pneumatico"
                value={form.marca_pneumatico}
                onChange={(e) => set("marca_pneumatico", e.target.value)}
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] bg-white font-bold"
              >
                {MARCHI_PNEUMATICO.map((m) => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <Label>Modello pneumatico</Label>
              <input
                data-testid="form-modello-pneumatico"
                value={form.modello_pneumatico}
                onChange={(e) => set("modello_pneumatico", e.target.value)}
                placeholder="Pilot Sport, Cinturato..."
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000]"
              />
            </div>
            <div>
              <Label>Misura *</Label>
              <input
                data-testid="form-misura"
                value={form.misura}
                onChange={(e) => set("misura", e.target.value)}
                placeholder="205/55 R16"
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] font-mono-data"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Stagionalità</Label>
                <select
                  data-testid="form-stagionalita"
                  value={form.stagionalita}
                  onChange={(e) => set("stagionalita", e.target.value)}
                  className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] bg-white font-bold"
                >
                  {STAGIONI.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label>Quantità</Label>
                <input
                  data-testid="form-quantita"
                  type="number"
                  min="1"
                  value={form.quantita}
                  onChange={(e) => set("quantita", e.target.value)}
                  className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] font-mono-data font-black"
                />
              </div>
            </div>
          </div>
        </Section>

        {/* Magazzino */}
        <Section title="Magazzino & Posizione">
          <div className="space-y-4">
            <div>
              <Label>Magazzino *</Label>
              <div className="grid grid-cols-2 gap-3 max-w-md">
                {MAGAZZINI.map((m) => (
                  <button
                    type="button"
                    key={m}
                    data-testid={`form-magazzino-${m}`}
                    onClick={() => {
                      set("magazzino", m);
                      // Reset scaffale if not valid for new magazzino
                      const validScaffali = m === "Cupello" ? ["A","B","C","D","E","F"] : ["A","B","C"];
                      if (!validScaffali.includes(form.scaffale)) set("scaffale", "A");
                    }}
                    className={`border-2 border-black p-4 font-display font-black uppercase text-lg transition-colors ${
                      form.magazzino === m
                        ? "bg-[#E60000] text-white"
                        : "bg-white text-black hover:bg-black hover:text-white"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>

            <PositionPicker
              settore={form.settore}
              scaffale={form.scaffale}
              magazzino={form.magazzino}
              onChange={({ settore, scaffale }) => {
                set("settore", settore);
                set("scaffale", scaffale);
              }}
            />

            <div>
              <Label>Posizione aggiuntiva (opzionale)</Label>
              <input
                data-testid="form-posizione"
                value={form.posizione}
                onChange={(e) => set("posizione", e.target.value)}
                placeholder="es. ripiano alto, fila 2..."
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000]"
              />
            </div>
          </div>
        </Section>

        <Section title="Note">
          <textarea
            data-testid="form-note"
            rows={3}
            value={form.note}
            onChange={(e) => set("note", e.target.value)}
            placeholder="Note aggiuntive..."
            className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] resize-none"
          />
        </Section>

        <div className="flex justify-end gap-3 sticky bottom-0 bg-white border-t-4 border-black -mx-4 md:-mx-8 px-4 md:px-8 py-4">
          <button
            type="button"
            onClick={() => navigate("/depositi")}
            className="border-2 border-black px-5 py-3 font-bold uppercase text-xs tracking-widest hover:bg-black hover:text-white"
          >
            Annulla
          </button>
          <button
            type="submit"
            data-testid="form-submit"
            disabled={saving}
            className="bg-[#E60000] text-white border-2 border-black px-6 py-3 font-bold uppercase tracking-widest hover:bg-black inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" strokeWidth={3} />
            {saving ? "Salvataggio..." : (editing ? "Aggiorna" : "Crea deposito")}
          </button>
        </div>
      </form>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="border-2 border-black bg-white p-5 md:p-6">
    <h2 className="font-display font-black text-lg uppercase tracking-tight mb-4 pb-3 border-b-2 border-black">
      {title}
    </h2>
    {children}
  </section>
);

const Label = ({ children }) => (
  <label className="block text-[10px] font-black uppercase tracking-widest mb-1.5">{children}</label>
);
